import {
  conditional,
  email,
  integer,
  maxLength,
  messageDefaults,
  packageSchemaForFormik,
  phone,
  required,
} from '@ahmdigital/form-validation';
import { FieldValue, FormValues, Schema } from '@ahmdigital/form-validation/lib/validators/types';
import { PRODUCT_TYPE } from '@ahmdigital/constants';
import { values } from 'lodash/fp';

import {
  AGE_FIELD_ERROR_MESSAGES,
  AGE_SECTION_FIELDS,
  FIELD_NAMES,
  LEAD_CAPTURE_FIELDS,
} from '../../../data/constants';
import { HealthFormValues } from '../../../types';
import { validateName } from '../../../../buy/components/buy-form/utils/name-validation/name-validation';
import ageValidationBuilder from '../utils/age-validation-builder';
import getAgeSectionErrorMessage from '../utils/get-age-section-error-message';
import getHasPartner from '../../../utils/get-has-partner';
import hasErrors from '../utils/has-errors';

const { AGE, PARTNER_AGE } = AGE_SECTION_FIELDS;
const { AGE_SECTION } = FIELD_NAMES;

const { integer: integerMessage, minAge, minAgePartner, validAge } = messageDefaults;

const leadCaptureFormSchema: Schema = {
  [LEAD_CAPTURE_FIELDS.EMAIL]: {
    validation: [email(), maxLength(255)],
  },
  [LEAD_CAPTURE_FIELDS.NAME]: {
    validation: [(value?: FieldValue) => validateName(value, 'Given name'), maxLength(24)],
  },
  [LEAD_CAPTURE_FIELDS.PHONE]: {
    validation: [phone()],
  },
};

// TODO: [AP-1621] We should reuse src/client/features/refine-price/schema/index.ts, but would require embedded quote widget to switch to Yup
const generateSchema = ({
  age,
  partnerAge,
  productType,
  scale,
  state,
  rebate,
  incomeType,
  incomeTier,
  widgetErrors,
}: HealthFormValues) => {
  const schema: Schema = {
    [AGE]: {
      initialValue: age,
      validation: [
        required(AGE_FIELD_ERROR_MESSAGES.AGE),
        integer(integerMessage),
        (value?: FieldValue) =>
          ageValidationBuilder({ empty: AGE_FIELD_ERROR_MESSAGES.AGE, max: validAge, min: minAge })(value),
      ],
    },
    [AGE_SECTION]: {
      initialValue: '',
      validation: [
        conditional(
          () => hasErrors(values(AGE_SECTION_FIELDS), widgetErrors),
          [
            (value?: FieldValue, formValues?: FormValues) =>
              required(getAgeSectionErrorMessage(widgetErrors, formValues?.scale as string))(value),
          ],
        ),
      ],
    },
    [PARTNER_AGE]: {
      initialValue: partnerAge,
      validation: [
        conditional(
          (formValues?: FormValues) => getHasPartner(formValues?.scale as string),
          [
            required(AGE_FIELD_ERROR_MESSAGES.PARTNER_AGE),
            integer(integerMessage),
            (value?: FieldValue) =>
              ageValidationBuilder({ empty: AGE_FIELD_ERROR_MESSAGES.PARTNER_AGE, max: validAge, min: minAgePartner })(
                value,
              ),
          ],
        ),
      ],
    },
    incomeTier: {
      initialValue: incomeTier,
    },
    incomeType: {
      initialValue: incomeType,
    },
    productType: {
      initialValue: productType || PRODUCT_TYPE.EXTRAS,
    },
    rebate: {
      initialValue: rebate,
    },
    scale: {
      initialValue: scale,
    },
    state: {
      initialValue: state,
    },

    ...leadCaptureFormSchema,
  };
  return { packagedSchema: packageSchemaForFormik(schema), rawSchema: schema };
};

export default generateSchema;
